import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import { useMenu } from './MenuProvider';
import { useDrawer } from './context/DrawerContext';
import { SvgIconComponent } from '@mui/icons-material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { Link, useLocation } from 'react-router-dom';
// Importa todos los posibles iconos
import GroupsTwoToneIcon from '@mui/icons-material/GroupsTwoTone';
import HomeWorkTwoToneIcon from '@mui/icons-material/HomeWorkTwoTone';
import EmojiPeopleTwoToneIcon from '@mui/icons-material/EmojiPeopleTwoTone';
import NaturePeopleTwoToneIcon from '@mui/icons-material/NaturePeopleTwoTone';
import MonetizationOnTwoToneIcon from '@mui/icons-material/MonetizationOnTwoTone';
import MultipleStopTwoToneIcon from '@mui/icons-material/MultipleStopTwoTone';
import MoneyTwoToneIcon from '@mui/icons-material/MoneyTwoTone';
import DatasetIcon from '@mui/icons-material/Dataset';
import ListAltIcon from '@mui/icons-material/ListAlt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import EditNoteIcon from '@mui/icons-material/EditNote';
import UpdateIcon from '@mui/icons-material/Update';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import HikingIcon from '@mui/icons-material/Hiking';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import CalculateIcon from '@mui/icons-material/Calculate';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import ChromeReaderModeIcon from '@mui/icons-material/ChromeReaderMode';
import PaddingIcon from '@mui/icons-material/Padding';
import PagesIcon from '@mui/icons-material/Pages';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import RouteIcon from '@mui/icons-material/Route';
import MoneyIcon from '@mui/icons-material/Money';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import ImportantDevicesTwoToneIcon from '@mui/icons-material/ImportantDevicesTwoTone';
import ManageAccountsTwoToneIcon from '@mui/icons-material/ManageAccountsTwoTone';
import CardTravelTwoToneIcon from '@mui/icons-material/CardTravelTwoTone';
import ArticleTwoToneIcon from '@mui/icons-material/ArticleTwoTone';
import AssessmentTwoToneIcon from '@mui/icons-material/AssessmentTwoTone';
import WorkTwoToneIcon from '@mui/icons-material/WorkTwoTone';
import AirplanemodeInactiveIcon from '@mui/icons-material/AirplanemodeInactive';
import { showMenuIcons } from '../utils/menu-ui';

const iconMap: { [key: string]: SvgIconComponent } = {
  GroupsTwoToneIcon,
  WorkTwoToneIcon,
  AssessmentTwoToneIcon,
  ArticleTwoToneIcon,
  CardTravelTwoToneIcon,
  ManageAccountsTwoToneIcon,
  HomeWorkTwoToneIcon,
  EmojiPeopleTwoToneIcon,
  NaturePeopleTwoToneIcon,
  MonetizationOnTwoToneIcon,
  MultipleStopTwoToneIcon,
  MoneyTwoToneIcon,
  DatasetIcon,
  ListAltIcon,
  RequestQuoteIcon,
  EditNoteIcon,
  UpdateIcon,
  AddBusinessIcon,
  FileUploadIcon,
  HikingIcon,
  SummarizeIcon,
  AppRegistrationIcon,
  AutoStoriesIcon,
  AvTimerIcon,
  CalculateIcon,
  CalendarViewWeekIcon,
  ChromeReaderModeIcon,
  PaddingIcon,
  PagesIcon,
  CurrencyExchangeIcon,
  PriceChangeIcon,
  RouteIcon,
  MoneyIcon,
  FileDownloadIcon,
  DeleteSweepIcon,
  PersonRemoveIcon,
  TransferWithinAStationIcon,
  ImportantDevicesTwoToneIcon,
  AirplanemodeInactiveIcon,
};

const DRAWER_WIDTH = 330;
const ICON_RAIL_WIDTH = 60;
const SHOW_MOBILE_ICON_RAIL = true;
const DRAWER_BACKGROUND = '#01274d';
const MODULE_RAIL_BACKGROUND = '#0768c9';
const STORAGE_KEYS = {
  module: 'selectedModule',
  menu: 'selectedMenu',
};
const STORAGE_PREFIXES = {
  module: 'module:',
  menu: 'menu:',
};

type MenuOption = {
  id: string;
  name: string;
  route: string;
  order: number;
  icon: string;
};

type DrawerModule = {
  id: string;
  name: string;
  order: number;
  icon: string;
  menu: MenuOption[];
};

const normalizeRoute = (route: string) => route.replace(/^\/+/, '').replace(/\/+$/, '');
const normalizePathname = (pathname: string) => normalizeRoute(pathname === '/' ? '' : pathname);
const routeToHref = (route: string) => {
  const normalizedRoute = normalizeRoute(route);
  return normalizedRoute ? `/${normalizedRoute}` : '/';
};

const matchesRoute = (pathname: string, route: string) => {
  const normalizedPath = normalizePathname(pathname);
  const normalizedRoute = normalizeRoute(route);

  if (!normalizedRoute) {
    return normalizedPath === '';
  }

  return (
    normalizedPath === normalizedRoute ||
    normalizedPath.startsWith(`${normalizedRoute}/`)
  );
};

const decodePrefixedId = (value: string | null, prefix: string) => {
  if (!value || !value.startsWith(prefix)) {
    return null;
  }

  return value.slice(prefix.length);
};

const getStoredValue = (key: string) => {
  if (typeof window === 'undefined') {
    return null;
  }

  return localStorage.getItem(key);
};

const setStoredValue = (key: string, value: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem(key, value);
};

const removeStoredValue = (key: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.removeItem(key);
};

const dedupeByKey = <T,>(items: T[], getKey: (item: T) => string): T[] => {
  const seen = new Set<string>();
  const unique: T[] = [];

  for (const item of items) {
    const key = getKey(item);
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    unique.push(item);
  }

  return unique;
};

const DrawerMenu: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { drawerOpen, menuOpen, closeDrawer, openDrawer } = useDrawer();
  const { modules } = useMenu();
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [selectedMenu, setSelectedMenu] = useState<string | null>(null);

  const normalizedModules = useMemo<DrawerModule[]>(() => {
    const filtered = (modules || [])
      .filter(rawModule => rawModule && rawModule.id && Array.isArray(rawModule.menu) && rawModule.menu.length > 0)
      .map(rawModule => ({
        id: String(rawModule.id),
        name: rawModule.name,
        order: rawModule.order,
        icon: rawModule.icon,
        menu: dedupeByKey(
          (rawModule.menu || [])
            .filter(rawMenu => rawMenu && rawMenu.id !== undefined && rawMenu.route)
            .map(rawMenu => ({
              id: String(rawMenu.id),
              name: rawMenu.name,
              route: normalizeRoute(rawMenu.route),
              order: rawMenu.order,
              icon: rawMenu.icon,
            })),
          menuItem => `${menuItem.id}:${menuItem.route}`
        ).sort((left, right) => left.order - right.order),
      }))
      .filter(module => module.menu.length > 0)
      .sort((left, right) => left.order - right.order);

    return dedupeByKey(filtered, module => module.id);
  }, [modules]);

  const getModuleIdForSelection = useCallback((selectionId: string | null): string | null => {
    if (!selectionId) {
      return null;
    }

    if (selectionId.startsWith(STORAGE_PREFIXES.module)) {
      return selectionId.replace(STORAGE_PREFIXES.module, '') || null;
    }

    if (selectionId.startsWith(STORAGE_PREFIXES.menu)) {
      const menuId = selectionId.replace(STORAGE_PREFIXES.menu, '');
      for (const module of normalizedModules) {
        if (module.menu.some(menuItem => menuItem.id === menuId)) {
          return module.id;
        }
      }
      return null;
    }

    for (const module of normalizedModules) {
      if (module.id === selectionId) {
        return module.id;
      }

      if (module.menu.some(menuItem => menuItem.id === selectionId)) {
        return module.id;
      }
    }

    return null;
  }, [normalizedModules]);

  useEffect(() => {
    const storedSelectedMenu = getStoredValue(STORAGE_KEYS.menu);
    const storedSelectedModule = getStoredValue(STORAGE_KEYS.module);

    if (storedSelectedMenu) {
      setSelectedMenu(storedSelectedMenu);
      return;
    }

    if (storedSelectedModule) {
      setSelectedMenu(storedSelectedModule);
    }
  }, []);

  useEffect(() => {
    const matchedModule = normalizedModules.find(module =>
      module.menu.some(menuItem => matchesRoute(location.pathname, menuItem.route))
    );

    if (matchedModule) {
      const matchedMenu = matchedModule.menu.find(menuItem => matchesRoute(location.pathname, menuItem.route));
      const moduleSelection = `${STORAGE_PREFIXES.module}${matchedModule.id}`;
      const menuSelection = matchedMenu ? `${STORAGE_PREFIXES.menu}${matchedMenu.id}` : moduleSelection;

      setActiveModuleId(matchedModule.id);
      setActiveMenuId(matchedMenu?.id || null);
      setSelectedMenu(menuSelection);
      setStoredValue(STORAGE_KEYS.module, moduleSelection);
      setStoredValue(STORAGE_KEYS.menu, menuSelection);
      return;
    }

    if (matchesRoute(location.pathname, '') || matchesRoute(location.pathname, 'dashboard')) {
      setActiveModuleId(null);
      setActiveMenuId(null);
      setSelectedMenu('home');
    }
  }, [location.pathname, normalizedModules]);

  useEffect(() => {
    if (normalizedModules.length === 0) {
      setActiveModuleId(null);
      setActiveMenuId(null);
      return;
    }

    if (!activeModuleId) {
      const inferred = getModuleIdForSelection(selectedMenu);
      setActiveModuleId(inferred ?? normalizedModules[0].id);
      return;
    }

    const exists = normalizedModules.some(module => module.id === activeModuleId);
    if (!exists) {
      setActiveModuleId(normalizedModules[0].id);
    }
  }, [activeModuleId, getModuleIdForSelection, normalizedModules, selectedMenu]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const inferred = getModuleIdForSelection(selectedMenu);
    if (inferred) {
      setActiveModuleId(inferred);
      return;
    }

    if (!activeModuleId && normalizedModules.length > 0) {
      setActiveModuleId(normalizedModules[0].id);
    }
  }, [activeModuleId, getModuleIdForSelection, menuOpen, normalizedModules, selectedMenu]);

  const activeModule = normalizedModules.find(module => module.id === activeModuleId) || null;
  const closeNavigationDrawer = () => {
    if (isMobile) {
      closeDrawer();
    }
  };

  const rail = (
    <Box
      sx={{
        width: ICON_RAIL_WIDTH,
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        py: 1,
        bgcolor: MODULE_RAIL_BACKGROUND,
      }}
    >
      <List dense sx={{ py: 0 }}>
        <Tooltip title="Inicio" placement="right" arrow enterDelay={200}>
          <ListItemButton
            component={Link}
            to="/"
            onClick={() => {
              setSelectedMenu('home');
              setActiveMenuId(null);
              setActiveModuleId(null);
              removeStoredValue(STORAGE_KEYS.module);
              setStoredValue(STORAGE_KEYS.menu, 'home');
              closeNavigationDrawer();
            }}
            sx={{ justifyContent: 'center', py: 1.1 }}
            selected={selectedMenu === 'home'}
          >
            <ListItemIcon sx={{ minWidth: 0, color: '#ffffff' }}>
              <HomeIcon />
            </ListItemIcon>
          </ListItemButton>
        </Tooltip>
      </List>

      <Divider />

      <List dense sx={{ py: 0 }}>
        {normalizedModules.map(module => (
          <Tooltip
            key={module.id}
            title={module.name || 'Modulo'}
            placement="right"
            arrow
            enterDelay={200}
          >
            <ListItemButton
              onClick={() => {
                const selectionKey = `${STORAGE_PREFIXES.module}${module.id}`;
                setActiveModuleId(module.id);
                setActiveMenuId(null);
                setSelectedMenu(selectionKey);
                setStoredValue(STORAGE_KEYS.module, selectionKey);
                setStoredValue(STORAGE_KEYS.menu, selectionKey);
                openDrawer();
              }}
              sx={{ justifyContent: 'center', py: 1.1 }}
              selected={activeModuleId === module.id}
            >
              <ListItemIcon sx={{ minWidth: 0, color: '#ffffff' }}>
                {React.createElement(iconMap[module.icon] || ViewModuleIcon)}
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        ))}
      </List>
    </Box>
  );

  const panel = (
    <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, bgcolor: DRAWER_BACKGROUND }}>
      {activeModule ? (
        <>
          <Typography variant="subtitle1" sx={{ color: '#ffffff', fontWeight: 700, px: 1, pb: 1 }}>
            {String(activeModule.name || '').toUpperCase()}
          </Typography>
          <List sx={{ width: '100%', py: 0 }}>
            {activeModule.menu.map(menuItem => {
              const selectionKey = `${STORAGE_PREFIXES.menu}${menuItem.id}`;
              const selected = selectedMenu === selectionKey || matchesRoute(location.pathname, menuItem.route);

              return (
                <ListItemButton
                  key={`${menuItem.id}:${menuItem.route}`}
                  component={Link}
                  to={routeToHref(menuItem.route)}
                  selected={selected}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    alignItems: 'center',
                    color: '#ffffff',
                    '& .MuiListItemIcon-root': {
                      color: '#ffffff',
                    },
                  }}
                  onClick={() => {
                    setActiveMenuId(menuItem.id);
                    setSelectedMenu(selectionKey);
                    setStoredValue(STORAGE_KEYS.module, `${STORAGE_PREFIXES.module}${activeModule.id}`);
                    setStoredValue(STORAGE_KEYS.menu, selectionKey);
                    closeNavigationDrawer();
                  }}
                >
                  {showMenuIcons && (
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      {React.createElement(iconMap[menuItem.icon] || ViewModuleIcon)}
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={menuItem.name}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: 1.2,
                      whiteSpace: 'normal',
                      color: '#ffffff',
                      textTransform: 'uppercase',
                    }}
                    sx={{
                      ml: showMenuIcons ? 0 : 0.5,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary', px: 1 }}>
          No hay opciones disponibles.
        </Typography>
      )}
    </Box>
  );

  const desktopDrawer = (
    <Drawer
      variant="permanent"
      open
      sx={{
        width: drawerOpen ? DRAWER_WIDTH : ICON_RAIL_WIDTH,
        flexShrink: 0,
        display: { xs: 'none', sm: 'block' },
        '& .MuiDrawer-paper': {
          width: drawerOpen ? DRAWER_WIDTH : ICON_RAIL_WIDTH,
          overflowX: 'hidden',
          height: '100vh',
          top: 0,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: DRAWER_BACKGROUND,
        },
      }}
    >
      <Toolbar />
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {rail}
        {drawerOpen && panel}
      </Box>
    </Drawer>
  );

  const mobileRailDrawer = (
    <Drawer
      variant="permanent"
      open
      sx={{
        width: ICON_RAIL_WIDTH,
        flexShrink: 0,
        display: { xs: 'block', sm: 'none' },
        '& .MuiDrawer-paper': {
          width: ICON_RAIL_WIDTH,
          overflowX: 'hidden',
          height: '100vh',
          top: 0,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: DRAWER_BACKGROUND,
        },
      }}
    >
      <Toolbar />
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {rail}
      </Box>
    </Drawer>
  );

  const mobilePanelDrawer = (
    <Drawer
      variant="temporary"
      open={menuOpen}
      onClose={closeDrawer}
      hideBackdrop
      ModalProps={{ keepMounted: true, disableScrollLock: true }}
      sx={{
        display: { xs: 'block', sm: 'none' },
        pointerEvents: 'none',
        '& .MuiDrawer-paper': {
          left: `${ICON_RAIL_WIDTH}px`,
          width: `calc(92vw - ${ICON_RAIL_WIDTH}px)`,
          maxWidth: `${DRAWER_WIDTH - ICON_RAIL_WIDTH}px`,
          overflowX: 'hidden',
          height: '100vh',
          top: 0,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          pointerEvents: 'auto',
          bgcolor: DRAWER_BACKGROUND,
        },
      }}
    >
      <Toolbar />
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {panel}
      </Box>
    </Drawer>
  );

  const mobileFullDrawer = (
    <Drawer
      variant="temporary"
      open={menuOpen}
      onClose={closeDrawer}
      ModalProps={{ keepMounted: true, disableScrollLock: true }}
      sx={{
        display: { xs: 'block', sm: 'none' },
        '& .MuiDrawer-paper': {
          width: '92vw',
          maxWidth: `${DRAWER_WIDTH}px`,
          overflowX: 'hidden',
          height: '100vh',
          top: 0,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: DRAWER_BACKGROUND,
        },
      }}
    >
      <Toolbar />
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {rail}
        {panel}
      </Box>
    </Drawer>
  );

  return (
    <>
      {desktopDrawer}
      {isMobile && SHOW_MOBILE_ICON_RAIL ? mobileRailDrawer : null}
      {isMobile && (SHOW_MOBILE_ICON_RAIL ? mobilePanelDrawer : mobileFullDrawer)}
    </>
  );
};

export default DrawerMenu;
